import React, { useState, useEffect } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import {
  Check,
  CheckCircle2,
  XCircle,
  Unlink,
  GitBranch,
  RefreshCw,
  Download,
  ArrowDown,
  Plus,
  ExternalLink,
  Loader2,
  FolderGit2,
  AlertCircle,
  ChevronDown,
  GitBranchPlus,
  CloudDownload,
  AlertTriangle,
} from 'lucide-react';
import GithubIcon from './GithubIcon';
import {
  useUserIntegrations,
  useConnectGitHub,
  useDeleteIntegration,
  USER_INTEGRATIONS_QUERY_KEY,
} from '../helpers/useUserSettings';
import { useGithubRepos, useGithubBranches } from '../helpers/useGithubApi';
import { getGithubTree } from '../endpoints/github/tree_GET.schema';
import { getGithubFile } from '../endpoints/github/file_GET.schema';
import { useBulkCreateFiles } from '../helpers/useBulkCreateFiles';
import { useQueryClient } from '../lib/queryClient';
import { getConnectedRepo, setConnectedRepo, ConnectedRepoInfo } from './GithubImportTab';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { toast } from 'sonner';
import { Checkbox } from './Checkbox';
import styles from './GitHubSettingsPanel.module.css';

interface GitHubSettingsPanelProps {
  sessionId: string;
}

export const GitHubSettingsPanel: React.FC<GitHubSettingsPanelProps> = ({ sessionId }) => {
  const queryClient = useQueryClient();
  const { data: integrations, isLoading: isLoadingIntegrations } = useUserIntegrations();
  const { connect } = useConnectGitHub();
  const deleteIntegration = useDeleteIntegration();

  // Session-level connected repo
  const [connectedRepo, setConnectedRepoState] = useState<ConnectedRepoInfo | null>(null);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showCreateRepoDialog, setShowCreateRepoDialog] = useState(false);
  const [showBranchDialog, setShowBranchDialog] = useState(false);
  const [selectedRepoToClone, setSelectedRepoToClone] = useState<string | null>(null);
  const [selectedBranchToClone, setSelectedBranchToClone] = useState<string | null>(null);
  const [newRepoName, setNewRepoName] = useState('');
  const [isCreatingRepo, setIsCreatingRepo] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [repoPat, setRepoPat] = useState('');
  const [showPatField, setShowPatField] = useState(false);

  // Create branch dialog state
  const [showCreateBranchDialog, setShowCreateBranchDialog] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [isCreatingBranch, setIsCreatingBranch] = useState(false);
  const [createBranchContext, setCreateBranchContext] = useState<'clone' | 'change'>('clone');

  // Import files state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    stage: 'tree' | 'files' | 'creating';
    current: number;
    total: number;
  } | null>(null);

  // Clone on connect option
  const [shouldCloneOnConnect, setShouldCloneOnConnect] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const bulkCreate = useBulkCreateFiles();

  const hasGithubIntegration = integrations?.integrations.some((i) => i.provider === 'github');
  const githubIntegration = integrations?.integrations.find((i) => i.provider === 'github');

  // Get GitHub username and detect GitHub App integration
  let githubUsername = '';
  let isGitHubAppIntegration = false;
  if (githubIntegration) {
    githubUsername = githubIntegration.providerUsername || '';
    try {
      const meta =
        typeof githubIntegration.metadata === 'string'
          ? JSON.parse(githubIntegration.metadata)
          : githubIntegration.metadata;
      isGitHubAppIntegration = meta?.type === 'github_app';
    } catch {}
  }

  // Fetch repos when connected
  const {
    data: reposData,
    isLoading: isLoadingRepos,
    error: reposError,
    refetch: refetchRepos,
  } = useGithubRepos(1, 100, hasGithubIntegration);

  // Handle GitHub token errors
  useEffect(() => {
    if (reposError) {
      console.error('Failed to fetch GitHub repos:', reposError);
      toast.error('Failed to connect to GitHub. Please reconnect your GitHub account.');
    }
  }, [reposError]);

  // Fetch branches for selected repo (either in clone dialog or for connected repo branch change)
  const repoForBranches = showBranchDialog ? connectedRepo?.repo : selectedRepoToClone;
  const { data: branchesData, isLoading: isLoadingBranches } = useGithubBranches(
    repoForBranches || undefined,
    !!repoForBranches,
  );

  // Load connected repo from localStorage
  useEffect(() => {
    const repo = getConnectedRepo(sessionId);
    setConnectedRepoState(repo);
  }, [sessionId]);

  // Set default branch when branches load
  useEffect(() => {
    if (branchesData && !selectedBranchToClone) {
      setSelectedBranchToClone(branchesData.defaultBranch);
    }
  }, [branchesData, selectedBranchToClone]);

  const filteredRepos = reposData?.repos.filter((repo) =>
    repo.full_name.toLowerCase().includes(repoSearch.toLowerCase()),
  );

  const handleDisconnectGitHub = async () => {
    try {
      await deleteIntegration.mutateAsync('github');
      // Also clear session-level repo connection
      setConnectedRepo(sessionId, null);
      setConnectedRepoState(null);
    } catch (error) {
      console.error('Failed to disconnect GitHub:', error);
    }
  };

  const handleDisconnectRepo = () => {
    setConnectedRepo(sessionId, null);
    setConnectedRepoState(null);
    toast.success('Repository disconnected from session');
  };

  const handleConnectRepo = async (
    repoFullName: string,
    branch: string,
    cloneFiles: boolean = false,
  ) => {
    const info: ConnectedRepoInfo = {
      repo: repoFullName,
      branch,
      connectedAt: new Date().toISOString(),
    };

    setIsConnecting(true);

    try {
      // First, connect the repo
      setConnectedRepo(sessionId, info);
      setConnectedRepoState(info);

      // If cloning, import files from the repo
      if (cloneFiles) {
        setImportProgress({ stage: 'tree', current: 0, total: 0 });

        // Fetch tree
        const treeData = await getGithubTree({
          repo: repoFullName,
          branch: branch,
        });

        const filesToFetch = treeData.files;
        const totalFiles = filesToFetch.length;

        if (totalFiles === 0) {
          toast.warning('Repository is empty or has no compatible files');
        } else {
          setImportProgress({ stage: 'files', current: 0, total: totalFiles });

          const importedFiles = [];
          const BATCH_SIZE = 3;

          for (let i = 0; i < totalFiles; i += BATCH_SIZE) {
            const batch = filesToFetch.slice(i, i + BATCH_SIZE);

            const batchResults = await Promise.all(
              batch.map(async (fileNode) => {
                try {
                  const fileData = await getGithubFile({
                    repo: repoFullName,
                    path: fileNode.path,
                    branch: branch,
                  });

                  return {
                    fileName: fileNode.path.split('/').pop() || '',
                    filePath: fileNode.path.substring(0, fileNode.path.lastIndexOf('/')) || '/',
                    content: fileData.content,
                    language: detectLanguage(fileNode.path),
                  };
                } catch (e) {
                  console.error(`Failed to fetch ${fileNode.path}`, e);
                  return null;
                }
              }),
            );

            const validFiles = batchResults.filter((f): f is NonNullable<typeof f> => f !== null);
            importedFiles.push(...validFiles);

            setImportProgress((prev) =>
              prev ? { ...prev, current: Math.min(i + BATCH_SIZE, totalFiles) } : null,
            );

            if (i + BATCH_SIZE < totalFiles) {
              await delay(300);
            }
          }

          setImportProgress({
            stage: 'creating',
            current: importedFiles.length,
            total: totalFiles,
          });

          if (importedFiles.length > 0) {
            await bulkCreate.mutateAsync({
              sessionId,
              files: importedFiles,
            });

            toast.success(
              `Connected to ${repoFullName} and imported ${importedFiles.length} files`,
            );
            queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
          } else {
            toast.success(`Connected to ${repoFullName}`);
          }
        }
      } else {
        toast.success(`Connected to ${repoFullName}`);
      }

      setShowCloneDialog(false);
      setSelectedRepoToClone(null);
      setSelectedBranchToClone(null);
      setShouldCloneOnConnect(false);
    } catch (error) {
      console.error('Failed to connect/clone repo:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to connect repository');
    } finally {
      setImportProgress(null);
      setIsConnecting(false);
    }
  };

  const handleChangeBranch = (newBranch: string) => {
    if (!connectedRepo) return;
    const info: ConnectedRepoInfo = {
      ...connectedRepo,
      branch: newBranch,
    };
    setConnectedRepo(sessionId, info);
    setConnectedRepoState(info);
    setShowBranchDialog(false);
    setSelectedBranchToClone(null);
    toast.success(`Switched to branch: ${newBranch}`);
  };

  const handleCreateNewRepo = async () => {
    if (!newRepoName.trim()) {
      toast.error('Please enter a repository name');
      return;
    }

    setIsCreatingRepo(true);
    try {
      const reqBody: any = {
        name: newRepoName.trim(),
        description: `Created from Tasking.tech`,
        private: false,
        auto_init: true,
      };
      if (repoPat.trim()) {
        reqBody.pat = repoPat.trim();
      }

      const response = await fetch('/_api/github/create-repo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let parsedError = '';
        try {
          const superjson = (await import('superjson')).default;
          const maybe = superjson.parse<{ error?: string }>(errorText);
          parsedError = maybe?.error || '';
        } catch {
          try {
            parsedError = JSON.parse(errorText)?.error || '';
          } catch {
            parsedError = errorText || '';
          }
        }
        // If server says PAT is required, show the PAT field instead of closing
        if (parsedError.startsWith('PAT_REQUIRED:')) {
          setShowPatField(true);
          toast.error('A Personal Access Token is needed to create repositories with GitHub App.');
          return;
        }
        throw new Error(parsedError || 'Failed to create repository');
      }

      const text = await response.text();
      const superjson = (await import('superjson')).default;
      const data = superjson.parse<any>(text);

      // Connect to the new repo
      const info: ConnectedRepoInfo = {
        repo: data.full_name,
        branch: data.default_branch || 'main',
        connectedAt: new Date().toISOString(),
      };
      setConnectedRepo(sessionId, info);
      setConnectedRepoState(info);

      toast.success(`Repository ${data.full_name} created and connected!`);
      setShowCreateRepoDialog(false);
      setNewRepoName('');
      setRepoPat('');
      setShowPatField(false);

      // Refresh repos list
      refetchRepos();
    } catch (error) {
      console.error('Failed to create repo:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create repository');
    } finally {
      setIsCreatingRepo(false);
    }
  };

  const handleOpenCreateBranchDialog = (context: 'clone' | 'change') => {
    setCreateBranchContext(context);
    setNewBranchName('');
    setShowCreateBranchDialog(true);
  };

  const handleCreateNewBranch = async () => {
    if (!newBranchName.trim()) {
      toast.error('Please enter a branch name');
      return;
    }

    // Get the repo to create the branch on
    const repo = createBranchContext === 'clone' ? selectedRepoToClone : connectedRepo?.repo;
    if (!repo) {
      toast.error('No repository selected');
      return;
    }

    // Get the source branch (default branch of the repo)
    const sourceBranch =
      createBranchContext === 'clone'
        ? branchesData?.defaultBranch
        : connectedRepo?.branch || branchesData?.defaultBranch;

    setIsCreatingBranch(true);
    try {
      const response = await fetch('/_api/github/create-branch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repo,
          branchName: newBranchName.trim(),
          sourceBranch,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create branch');
      }

      toast.success(`Branch "${newBranchName}" created successfully!`);
      setShowCreateBranchDialog(false);
      setNewBranchName('');

      // Set the newly created branch as the selected branch
      setSelectedBranchToClone(newBranchName.trim());

      // Invalidate branches query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['github', 'branches', repo] });

      // If we're in 'change' context, also update the connected repo
      if (createBranchContext === 'change' && connectedRepo) {
        const info: ConnectedRepoInfo = {
          ...connectedRepo,
          branch: newBranchName.trim(),
        };
        setConnectedRepo(sessionId, info);
        setConnectedRepoState(info);
        setShowBranchDialog(false);
        toast.success(`Switched to new branch: ${newBranchName}`);
      }
    } catch (error) {
      console.error('Failed to create branch:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create branch');
    } finally {
      setIsCreatingBranch(false);
    }
  };

  // Helper to detect language from filename
  const detectLanguage = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || 'plaintext';
    if (['js', 'jsx', 'ts', 'tsx'].includes(ext)) return 'typescript';
    if (['py'].includes(ext)) return 'python';
    if (['html'].includes(ext)) return 'html';
    if (['css'].includes(ext)) return 'css';
    if (['json'].includes(ext)) return 'json';
    if (['md'].includes(ext)) return 'markdown';
    if (['sql'].includes(ext)) return 'sql';
    if (['java'].includes(ext)) return 'java';
    if (['go'].includes(ext)) return 'go';
    if (['rs'].includes(ext)) return 'rust';
    return 'plaintext';
  };

  // Helper for delay between batches
  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleImportFromGitHub = async () => {
    if (!connectedRepo) return;

    setIsImporting(true);
    setImportProgress({ stage: 'tree', current: 0, total: 0 });

    try {
      // 1. Fetch tree
      const treeData = await getGithubTree({
        repo: connectedRepo.repo,
        branch: connectedRepo.branch,
      });

      const filesToFetch = treeData.files;
      const totalFiles = filesToFetch.length;

      if (totalFiles === 0) {
        toast.error('No compatible files found in this repository');
        setImportProgress(null);
        setIsImporting(false);
        return;
      }

      setImportProgress({ stage: 'files', current: 0, total: totalFiles });

      const importedFiles = [];
      const BATCH_SIZE = 3;

      for (let i = 0; i < totalFiles; i += BATCH_SIZE) {
        const batch = filesToFetch.slice(i, i + BATCH_SIZE);

        const batchResults = await Promise.all(
          batch.map(async (fileNode) => {
            try {
              const fileData = await getGithubFile({
                repo: connectedRepo.repo,
                path: fileNode.path,
                branch: connectedRepo.branch,
              });

              return {
                fileName: fileNode.path.split('/').pop() || '',
                filePath: fileNode.path.substring(0, fileNode.path.lastIndexOf('/')) || '/',
                content: fileData.content,
                language: detectLanguage(fileNode.path),
              };
            } catch (e) {
              console.error(`Failed to fetch ${fileNode.path}`, e);
              return null;
            }
          }),
        );

        const validFiles = batchResults.filter((f): f is NonNullable<typeof f> => f !== null);
        importedFiles.push(...validFiles);

        setImportProgress((prev) =>
          prev ? { ...prev, current: Math.min(i + BATCH_SIZE, totalFiles) } : null,
        );

        if (i + BATCH_SIZE < totalFiles) {
          await delay(300);
        }
      }

      setImportProgress({ stage: 'creating', current: importedFiles.length, total: totalFiles });

      // 2. Bulk create files in session
      if (importedFiles.length > 0) {
        await bulkCreate.mutateAsync({
          sessionId,
          files: importedFiles,
        });

        toast.success(`Successfully imported ${importedFiles.length} files from GitHub`);
        setShowImportDialog(false);

        // Invalidate files query to refresh the file list
        queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
      } else {
        toast.error('Failed to fetch any file contents');
      }
    } catch (error) {
      console.error('Import failed', error);
      toast.error(
        'Failed to import from repository: ' +
          (error instanceof Error ? error.message : 'Unknown error'),
      );
    } finally {
      setImportProgress(null);
      setIsImporting(false);
    }
  };

  if (isLoadingIntegrations) {
    return (
      <div className={styles.container}>
        <Skeleton style={{ width: '100%', height: '150px' }} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* GitHub Account Connection Status */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            <GithubIcon size={18} connected={!!githubIntegration} />
            GitHub Account
          </h3>
        </div>

        {hasGithubIntegration ? (
          <div className={styles.connectedAccount}>
            <div className={styles.connectionStatus}>
              <CheckCircle2 size={20} className={styles.connectedIcon} />
              <div className={styles.accountInfo}>
                <span className={styles.accountLabel}>Connected as</span>
                <span className={styles.accountName}>{githubUsername}</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnectGitHub}
              disabled={deleteIntegration.isPending}
            >
              {deleteIntegration.isPending ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <Unlink size={14} />
                  Disconnect
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className={styles.notConnected}>
            <div className={styles.warningBanner}>
              <AlertCircle size={16} />
              <span>
                Connect your GitHub account to sync checkpoints to your repository. Checkpoints will
                be automatically pushed as commits when you create them.
              </span>
            </div>
            <div className={styles.connectionStatus}>
              <XCircle size={20} className={styles.disconnectedIcon} />
              <span>Not connected</span>
            </div>
            <Button onClick={connect} className={styles.connectButton}>
              <GithubIcon size={16} connected={false} />
              Connect GitHub
            </Button>
          </div>
        )}
      </div>

      {/* Session Repository Connection */}
      {hasGithubIntegration && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <FolderGit2 size={18} />
              Session Repository
            </h3>
          </div>

          {connectedRepo ? (
            <div className={styles.repoConnection}>
              <div className={styles.repoInfo}>
                <div className={styles.repoStatus}>
                  <CheckCircle2 size={16} className={styles.connectedIcon} />
                  <span className={styles.repoName}>{connectedRepo.repo}</span>
                </div>
                <button
                  className={styles.branchButton}
                  onClick={() => setShowBranchDialog(true)}
                  title="Click to change branch"
                >
                  <GitBranch size={14} />
                  <span>{connectedRepo.branch}</span>
                  <ChevronDown size={12} />
                </button>
              </div>
              <div className={styles.repoActions}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowImportDialog(true)}
                  title="Import files from GitHub"
                >
                  <CloudDownload size={14} />
                  Import
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => window.open(`https://github.com/${connectedRepo.repo}`, '_blank')}
                  title="Open on GitHub"
                >
                  <ExternalLink size={14} />
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisconnectRepo}>
                  <Unlink size={14} />
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.noRepoConnected}>
              <AlertCircle size={20} className={styles.warningIcon} />
              <p>No repository connected to this session</p>
              <div className={styles.repoActionButtons}>
                <Button variant="outline" onClick={() => setShowCloneDialog(true)}>
                  <Download size={16} />
                  Connect Existing Repo
                </Button>
                <Button variant="primary" onClick={() => setShowCreateRepoDialog(true)}>
                  <Plus size={16} />
                  Create New Repo
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clone/Connect Repository Dialog */}
      <Dialog
        open={showCloneDialog}
        onOpenChange={(open) => {
          if (!isConnecting) {
            setShowCloneDialog(open);
            if (!open) {
              // Reset state when dialog closes
              setSelectedRepoToClone(null);
              setSelectedBranchToClone(null);
              setShouldCloneOnConnect(false);
              setRepoSearch('');
            }
          }
        }}
      >
        <DialogContent className={styles.dialogContent}>
          <DialogHeader>
            <DialogTitle>Connect Repository</DialogTitle>
            <DialogDescription>
              Select a repository to connect to this session. Files will be synced with GitHub.
            </DialogDescription>
          </DialogHeader>

          <div className={styles.repoSelector}>
            <Input
              placeholder="Search repositories..."
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              className={styles.searchInput}
            />

            <div className={styles.repoList}>
              {isLoadingRepos ? (
                <div className={styles.loadingRepos}>
                  <Spinner size="sm" />
                  <span>Loading repositories...</span>
                </div>
              ) : filteredRepos?.length === 0 ? (
                <div className={styles.noRepos}>No repositories found</div>
              ) : (
                filteredRepos?.map((repo) => (
                  <button
                    key={repo.id}
                    className={`${styles.repoOption} ${selectedRepoToClone === repo.full_name ? styles.repoOptionSelected : ''}`}
                    onClick={() => {
                      setSelectedRepoToClone(repo.full_name);
                      setSelectedBranchToClone(repo.default_branch);
                    }}
                  >
                    {selectedRepoToClone === repo.full_name && (
                      <Check size={14} className={styles.checkIcon} />
                    )}
                    <span className={styles.repoOptionName}>{repo.full_name}</span>
                    {repo.private && <span className={styles.privateBadge}>Private</span>}
                  </button>
                ))
              )}
            </div>

            {selectedRepoToClone && branchesData && (
              <div className={styles.branchSelector}>
                <label>Branch:</label>
                <div className={styles.branchSelectorRow}>
                  <Select
                    value={selectedBranchToClone || ''}
                    onValueChange={setSelectedBranchToClone}
                  >
                    <SelectTrigger className={styles.branchTrigger}>
                      <SelectValue placeholder="Select branch..." />
                    </SelectTrigger>
                    <SelectContent>
                      {branchesData.branches.map((branch) => (
                        <SelectItem key={branch.name} value={branch.name}>
                          {branch.name} {branch.protected && '(protected)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenCreateBranchDialog('clone')}
                    title="Create a new branch"
                  >
                    <GitBranchPlus size={14} />
                    New
                  </Button>
                </div>
              </div>
            )}

            {selectedRepoToClone && selectedBranchToClone && (
              <div className={styles.cloneOption}>
                <label className={styles.checkboxLabel}>
                  <Checkbox
                    checked={shouldCloneOnConnect}
                    onChange={(e) => setShouldCloneOnConnect(e.target.checked)}
                    disabled={isConnecting}
                  />
                  <span>Clone files from repository</span>
                </label>
                {shouldCloneOnConnect && (
                  <div className={styles.cloneWarning}>
                    <AlertTriangle size={16} />
                    <span>
                      This will download all files from the repository into your session. Existing
                      files with the same paths will be overwritten.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {isConnecting && importProgress && (
            <div className={styles.importProgress}>
              <div className={styles.progressInfo}>
                {importProgress.stage === 'files' ? (
                  <ArrowDown size={20} className={styles.arrowBounce} />
                ) : (
                  <Loader2 size={20} className={styles.spinner} />
                )}
                <span>
                  {importProgress.stage === 'tree' && 'Fetching repository structure...'}
                  {importProgress.stage === 'files' &&
                    `Downloading files: ${importProgress.current} / ${importProgress.total}`}
                  {importProgress.stage === 'creating' &&
                    `Creating files in session: ${importProgress.current} files`}
                </span>
              </div>
              {importProgress.stage === 'files' && importProgress.total > 0 && (
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowCloneDialog(false)}
              disabled={isConnecting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedRepoToClone && selectedBranchToClone) {
                  handleConnectRepo(
                    selectedRepoToClone,
                    selectedBranchToClone,
                    shouldCloneOnConnect,
                  );
                }
              }}
              disabled={!selectedRepoToClone || !selectedBranchToClone || isConnecting}
            >
              {isConnecting ? (
                <>
                  <Loader2 size={16} className={styles.spinner} />
                  {shouldCloneOnConnect ? 'Cloning...' : 'Connecting...'}
                </>
              ) : (
                <>
                  {shouldCloneOnConnect ? <CloudDownload size={16} /> : <Download size={16} />}
                  {shouldCloneOnConnect ? 'Connect & Clone' : 'Connect Repository'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Repository Dialog */}
      <Dialog
        open={showCreateRepoDialog}
        onOpenChange={(open) => {
          setShowCreateRepoDialog(open);
          if (!open) {
            setRepoPat('');
            setShowPatField(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Repository</DialogTitle>
            <DialogDescription>
              Create a new GitHub repository and connect it to this session.
            </DialogDescription>
          </DialogHeader>

          <div className={styles.createRepoForm}>
            <div className={styles.formField}>
              <label>Repository Name</label>
              <div className={styles.repoNameInput}>
                <span className={styles.repoPrefix}>{githubUsername}/</span>
                <Input
                  placeholder="my-awesome-project"
                  value={newRepoName}
                  onChange={(e) => setNewRepoName(e.target.value.replace(/\s+/g, '-'))}
                />
              </div>
              <span className={styles.hint}>Use lowercase letters, numbers, and hyphens</span>
            </div>

            {(isGitHubAppIntegration || showPatField) && (
              <div className={styles.formField}>
                <label>
                  Personal Access Token{' '}
                  {!showPatField && <span className={styles.hint}>(optional)</span>}
                </label>
                <Input
                  id="github-pat"
                  name="github-pat"
                  type="password"
                  placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                  value={repoPat}
                  onChange={(e) => setRepoPat(e.target.value)}
                  autoComplete="off"
                />
                <span className={styles.hint}>
                  GitHub App can't create repos directly.{' '}
                  <a
                    href="https://github.com/settings/tokens/new?scopes=repo&description=Tasking.tech+repo+creation"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary)' }}
                  >
                    Generate a token
                  </a>{' '}
                  with <strong>repo</strong> scope.
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateRepoDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateNewRepo} disabled={isCreatingRepo || !newRepoName.trim()}>
              {isCreatingRepo ? (
                <>
                  <Loader2 size={16} className={styles.spinner} />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Create Repository
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Branch Dialog */}
      <Dialog open={showBranchDialog} onOpenChange={setShowBranchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Branch</DialogTitle>
            <DialogDescription>
              Select a branch for {connectedRepo?.repo}. Checkpoints will be synced to this branch.
            </DialogDescription>
          </DialogHeader>

          <div className={styles.branchSelector}>
            {isLoadingBranches ? (
              <div className={styles.loadingRepos}>
                <Spinner size="sm" />
                <span>Loading branches...</span>
              </div>
            ) : (
              <div className={styles.branchSelectorRow}>
                <Select
                  value={selectedBranchToClone || connectedRepo?.branch || 'main'}
                  onValueChange={setSelectedBranchToClone}
                >
                  <SelectTrigger className={styles.branchTrigger}>
                    <SelectValue placeholder="Select branch..." />
                  </SelectTrigger>
                  <SelectContent>
                    {branchesData?.branches.map((branch) => (
                      <SelectItem key={branch.name} value={branch.name}>
                        {branch.name} {branch.protected && '(protected)'}{' '}
                        {branch.name === branchesData.defaultBranch && '(default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenCreateBranchDialog('change')}
                  title="Create a new branch"
                >
                  <GitBranchPlus size={14} />
                  New
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowBranchDialog(false);
                setSelectedBranchToClone(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedBranchToClone) {
                  handleChangeBranch(selectedBranchToClone);
                }
              }}
              disabled={!selectedBranchToClone || selectedBranchToClone === connectedRepo?.branch}
            >
              <GitBranch size={16} />
              Switch Branch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Branch Dialog */}
      <Dialog open={showCreateBranchDialog} onOpenChange={setShowCreateBranchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Branch</DialogTitle>
            <DialogDescription>
              Create a new branch on{' '}
              {createBranchContext === 'clone' ? selectedRepoToClone : connectedRepo?.repo}. The new
              branch will be created from{' '}
              {createBranchContext === 'clone'
                ? branchesData?.defaultBranch
                : connectedRepo?.branch || branchesData?.defaultBranch}
              .
            </DialogDescription>
          </DialogHeader>

          <div className={styles.createRepoForm}>
            <div className={styles.formField}>
              <label>Branch Name</label>
              <Input
                placeholder="feature/my-new-feature"
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value.replace(/\s+/g, '-'))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newBranchName.trim()) {
                    handleCreateNewBranch();
                  }
                }}
              />
              <span className={styles.hint}>
                Use lowercase letters, numbers, hyphens, and forward slashes
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateBranchDialog(false);
                setNewBranchName('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewBranch}
              disabled={isCreatingBranch || !newBranchName.trim()}
            >
              {isCreatingBranch ? (
                <>
                  <Loader2 size={16} className={styles.spinner} />
                  Creating...
                </>
              ) : (
                <>
                  <GitBranchPlus size={16} />
                  Create Branch
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Files Dialog */}
      <Dialog
        open={showImportDialog}
        onOpenChange={(open) => {
          if (!isImporting) setShowImportDialog(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Files from GitHub</DialogTitle>
            <DialogDescription>
              {importProgress ? (
                <span>Importing files from {connectedRepo?.repo}...</span>
              ) : (
                <span>
                  This will download all files from <strong>{connectedRepo?.repo}</strong> (branch:{' '}
                  <strong>{connectedRepo?.branch}</strong>) into your session. Existing files with
                  the same names will be overwritten.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {importProgress ? (
            <div className={styles.importProgress}>
              <div className={styles.progressInfo}>
                <Loader2 size={20} className={styles.spinner} />
                <span>
                  {importProgress.stage === 'tree' && 'Fetching repository structure...'}
                  {importProgress.stage === 'files' &&
                    `Downloading files: ${importProgress.current} / ${importProgress.total}`}
                  {importProgress.stage === 'creating' &&
                    `Creating files in session: ${importProgress.current} files`}
                </span>
              </div>
              {importProgress.stage === 'files' && importProgress.total > 0 && (
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className={styles.importWarning}>
              <AlertTriangle size={20} className={styles.warningIcon} />
              <span>
                Files in your session with the same paths will be overwritten. Consider creating a
                checkpoint before importing.
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowImportDialog(false)}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button onClick={handleImportFromGitHub} disabled={isImporting}>
              {isImporting ? (
                <>
                  <Loader2 size={16} className={styles.spinner} />
                  Importing...
                </>
              ) : (
                <>
                  <CloudDownload size={16} />
                  Import Files
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
